import React, { useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Container, Form, Button, FloatingLabel, Alert } from "react-bootstrap";
import {
  signUp,
  confirmSignUp,
  resendSignUpCode,
  signIn,
} from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsSignedIn,
  setSignUpStep,
  setSignUpError,
  setSignUpMessage,
  clearSignUpState,
} from "../../redux/slices/authSlice";

export default function SignUp() {
  // State variables to store form inputs
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");

  const navigate = useNavigate(); // Initialize navigate function for navigation
  const dispatch = useDispatch(); // Initialize dispatch to interact with Redux

  // Access sign-up state from Redux store
  const signUpStep = useSelector((state) => state.auth.signUpStep);
  const signUpError = useSelector((state) => state.auth.signUpError);
  const signUpMessage = useSelector((state) => state.auth.signUpMessage);

  // Function to handle sign-up
  async function handleSignUp(e) {
    e.preventDefault(); // Prevent default form submission behavior
    try {
      // Sign up using AWS Amplify
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
          },
          autoSignIn: false,
        },
      });
      // Update the Redux state to move to the confirmation step
      dispatch(setSignUpStep("confirmSignUp"));
      setConfirmationCode(""); // Clear confirmation code for the new step
    } catch (error) {
      // Set error message in Redux state
      dispatch(setSignUpError(error.message));
    }
  }

  // Function to handle sign-up confirmation
  async function handleSignUpConfirmation(e) {
    e.preventDefault(); // Prevent default form submission behavior
    try {
      // Confirm sign-up using AWS Amplify
      await confirmSignUp({ username: email, confirmationCode });
      // Automatically sign in the user after confirmation
      await signIn({ username: email, password });
      // Update Redux state to indicate user is signed in
      dispatch(setIsSignedIn(true));
      // Clear sign-up state in Redux store
      dispatch(clearSignUpState());
      navigate("/"); // Navigate to the home page
    } catch (error) {
      // Set error message in Redux state
      dispatch(setSignUpError(error.message));
    }
  }

  // Function to resend confirmation code
  async function handleResendCode() {
    try {
      // Resend the confirmation code using AWS Amplify
      await resendSignUpCode(email);
      // Set success message in Redux state
      dispatch(
        setSignUpMessage("A new confirmation code has been sent to your email.")
      );
    } catch (error) {
      // Set error message in Redux state
      dispatch(setSignUpError(error.message));
    }
  }

  // Function to mask email for privacy
  function maskEmail(email) {
    const [localPart, domainPart] = email.split("@");
    const maskedLocalPart = `${localPart[0]}***${
      localPart[localPart.length - 1]
    }`;
    const maskedDomainPart = `${domainPart[0]}***${
      domainPart[domainPart.length - 1]
    }`;
    return `${maskedLocalPart}@${maskedDomainPart}`;
  }

  return (
    <Container className="d-flex align-items-center justify-content-center py-4 min-vh-100">
      {signUpStep === "signUp" ? (
        // Render sign-up form
        <SignUpForm
          email={email}
          password={password}
          setEmail={setEmail}
          setPassword={setPassword}
          handleSignUp={handleSignUp}
          error={signUpError}
        />
      ) : (
        // Render confirmation form
        <ConfirmationForm
          email={email}
          confirmationCode={confirmationCode}
          setConfirmationCode={setConfirmationCode}
          handleSignUpConfirmation={handleSignUpConfirmation}
          handleResendCode={handleResendCode}
          error={signUpError}
          message={signUpMessage}
          maskEmail={maskEmail}
        />
      )}
    </Container>
  );
}

// Subcomponent for the sign-up form
function SignUpForm({
  email,
  password,
  setEmail,
  setPassword,
  handleSignUp,
  error,
}) {
  return (
    <Form className="form-signin w-100" onSubmit={handleSignUp}>
      <h1 className="h3 mb-3 fw-normal">Please register</h1>
      {error && <Alert variant="danger">{error}</Alert>}{" "}
      {/* Display error message if any */}
      <FloatingLabel
        controlId="floatingEmail"
        label="Email address"
        className="mb-3"
      >
        <Form.Control
          className="rounded-0 rounded-top mb-3"
          type="email"
          placeholder="name@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)} // Update email state on change
          required
        />
      </FloatingLabel>
      <FloatingLabel
        controlId="floatingPassword"
        label="Password"
        className="mb-3"
      >
        <Form.Control
          className="rounded-0 rounded-bottom mb-3"
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)} // Update password state on change
          required
        />
      </FloatingLabel>
      <Form.Group className="mb-3" controlId="formBasicCheckbox">
        <Form.Check type="checkbox" label="Remember me" />
      </Form.Group>
      <Button className="w-50 me-2" variant="primary" type="submit">
        Sign Up
      </Button>
    </Form>
  );
}

// Subcomponent for the confirmation form
function ConfirmationForm({
  email,
  confirmationCode,
  setConfirmationCode,
  handleSignUpConfirmation,
  handleResendCode,
  error,
  message,
  maskEmail,
}) {
  return (
    <Form className="form-signin w-100" onSubmit={handleSignUpConfirmation}>
      <h1 className="h3 mb-3 fw-normal">We Emailed You</h1>
      <p>
        Your code is on the way. To log in, enter the code we emailed to{" "}
        {maskEmail(email)}. It may take a minute to arrive.
      </p>
      {error && <Alert variant="danger">{error}</Alert>}{" "}
      {/* Display error message if any */}
      {message && <Alert variant="success">{message}</Alert>}{" "}
      {/* Display success message if any */}
      <FloatingLabel
        controlId="floatingConfirmationCode"
        label="Confirmation Code"
        className="mb-3"
      >
        <Form.Control
          className="rounded-0 rounded-bottom mb-3"
          type="text"
          placeholder="Enter your code"
          value={confirmationCode}
          onChange={(e) => setConfirmationCode(e.target.value)} // Update confirmation code state on change
          required
        />
      </FloatingLabel>
      <Button className="w-50 me-2" variant="primary" type="submit">
        Confirm
      </Button>
      <Button
        className="w-50"
        variant="secondary"
        type="button"
        onClick={handleResendCode}
      >
        Resend Code
      </Button>
    </Form>
  );
}
